<template>
  <Teleport to="body">
    <Transition name="video-modal">
      <div v-if="isOpen" class="video-player-overlay" @click="close">
        <div class="video-player" @click.stop>
          <!-- Close button -->
          <button class="video-player__close" @click="close">
            <v-icon size="24">mdi-close</v-icon>
          </button>

          <!-- Video container -->
          <div class="video-player__container">
            <video
              ref="videoRef"
              :src="videoSrc"
              :poster="posterUrl"
              class="video-player__video"
              controls
              autoplay
              playsinline
              @timeupdate="onTimeUpdate"
              @seeked="onSeeked"
              @ended="onEnded"
              @error="onError"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          <!-- Video info -->
          <div v-if="currentVideo" class="video-player__info">
            <h3 class="video-player__title">
              {{ currentVideo.title || "Untitled" }}
            </h3>
            <p v-if="currentVideoDescription" class="video-player__description">
              {{ currentVideoDescription }}
            </p>
            <div class="video-player__meta">
              <span v-if="viewCount" class="video-player__views">
                <v-icon size="16">mdi-eye-outline</v-icon>
                {{ formatNumber(viewCount) }} views
              </span>
              <span v-if="currentVideo.createdAt" class="video-player__date">
                {{ formatDate(currentVideo.createdAt) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import type { Post } from "@/types";
import { videoViewTracker } from "@/services/videoViewTracker";
import { useSdk } from "@/composables/useSdk";
import { useAuth } from "@/composables/useAuth";
import { formatBodyAsPlainText } from "@/utils/postUtils";

const { sdk } = useSdk();
const { isAuthenticated } = useAuth();

const isOpen = ref(false);
const currentVideo = ref<Post | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);

// Computed
const videoSrc = computed(() => {
  if (!currentVideo.value) return "";
  const urls = currentVideo.value.videoUrls;
  // Prefer MP4 for broader compatibility, fall back to HLS
  return (
    urls?.mp4 || urls?.hls || (currentVideo.value.streamUrl as string) || ""
  );
});

const posterUrl = computed(() => {
  if (!currentVideo.value) return "";
  const urls = currentVideo.value.videoUrls;
  if (urls?.thumbnail) return urls.thumbnail;
  // Fall back to first image if available
  const images = currentVideo.value.images as
    | Array<{ url?: string }>
    | undefined;
  return images?.[0]?.url || "";
});

const viewCount = computed(() => {
  return currentVideo.value?.postEngagement?.viewCount || 0;
});

const currentVideoDescription = computed(() => {
  const raw = currentVideo.value?.content || currentVideo.value?.body || "";
  return typeof raw === "string" ? formatBodyAsPlainText(raw) : "";
});

// Format helpers
function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1) + "M";
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1) + "K";
  }
  return num.toString();
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Methods
function open(video: Post) {
  currentVideo.value = video;
  isOpen.value = true;
  // Prevent body scroll
  document.body.style.overflow = "hidden";
}

function close() {
  const ulid = currentVideo.value?.ulid;
  if (videoRef.value) {
    // Track final segment before closing
    if (ulid) {
      videoViewTracker.trackEnded(ulid, videoRef.value.duration || 0);
      videoViewTracker.resetPost(ulid);
    }
    videoRef.value.pause();
  }
  isOpen.value = false;
  currentVideo.value = null;
  document.body.style.overflow = "";
}

function onTimeUpdate() {
  const video = videoRef.value;
  const ulid = currentVideo.value?.ulid;
  if (!video || !ulid) return;
  videoViewTracker.trackTimeUpdate(ulid, video.currentTime, video.duration);
}

function onSeeked() {
  const video = videoRef.value;
  const ulid = currentVideo.value?.ulid;
  if (!video || !ulid) return;
  videoViewTracker.onSeek(ulid, video.currentTime);
}

function onEnded() {
  const video = videoRef.value;
  const ulid = currentVideo.value?.ulid;
  if (video && ulid) {
    videoViewTracker.trackEnded(ulid, video.duration);
  }
}

function onError(e: Event) {
  console.error("Video playback error:", e);
}

// Keyboard handler
function handleKeyboard(e: KeyboardEvent) {
  if (!isOpen.value) return;

  switch (e.code) {
    case "Escape":
      close();
      break;
    case "Space":
      e.preventDefault();
      if (videoRef.value) {
        if (videoRef.value.paused) {
          videoRef.value.play();
        } else {
          videoRef.value.pause();
        }
      }
      break;
  }
}

onMounted(() => {
  videoViewTracker.initialize(sdk, isAuthenticated.value);
  window.addEventListener("keydown", handleKeyboard);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyboard);
  document.body.style.overflow = "";
});

// Expose methods
defineExpose({
  open,
  close,
  isOpen,
  currentVideo,
});
</script>

<style scoped lang="scss">
.video-player-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-video-player);
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.video-player {
  position: relative;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  &__close {
    position: absolute;
    top: -48px;
    right: 0;
    width: 40px;
    height: 40px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.1);
    border-radius: 50%;
    color: rgb(var(--v-theme-on-surface));
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
    z-index: var(--z-inline-elevated);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &__container {
    position: relative;
    width: 100%;
    background: #000;
    border-radius: 12px;
    overflow: hidden;
  }

  &__video {
    width: 100%;
    max-height: 70vh;
    object-fit: contain;
    background: #000;
  }

  &__info {
    padding: 16px 0;
    color: rgb(var(--v-theme-on-surface));
  }

  &__title {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0 0 8px;
  }

  &__description {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin: 0 0 12px;
    line-height: 1.5;
    max-height: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  &__views,
  &__date {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

// Transition
.video-modal-enter-active,
.video-modal-leave-active {
  transition: opacity 0.3s ease;

  .video-player {
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }
}

.video-modal-enter-from,
.video-modal-leave-to {
  opacity: 0;

  .video-player {
    transform: scale(0.95);
    opacity: 0;
  }
}

// Responsive
@media (max-width: 768px) {
  .video-player-overlay {
    padding: 0;
  }

  .video-player {
    max-width: 100%;
    max-height: 100vh;

    &__close {
      top: 16px;
      right: 16px;
    }

    &__container {
      border-radius: 0;
    }

    &__video {
      max-height: 50vh;
    }

    &__info {
      padding: 16px;
    }
  }
}
</style>
